import { useEffect, useRef, useState } from "react";
import { lovable } from "@/integrations/lovable";
import {
  isNativeAppleAuthAvailable,
  NATIVE_APPLE_SIGN_IN_ENABLED,
  signInWithNativeApple,
} from "@/lib/native-apple-auth";
import { hasLiveSession, mapOAuthError } from "@/lib/oauth-signin";

function AppleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" aria-hidden>
      <path d="M17.05 12.04c-.03-2.62 2.14-3.88 2.24-3.94-1.22-1.79-3.13-2.04-3.81-2.07-1.62-.16-3.17.95-3.99.95-.83 0-2.09-.93-3.44-.9-1.77.03-3.42 1.03-4.34 2.61-1.86 3.22-.48 7.99 1.33 10.61.88 1.28 1.93 2.71 3.31 2.66 1.33-.05 1.84-.86 3.45-.86 1.61 0 2.07.86 3.49.83 1.44-.03 2.36-1.3 3.25-2.59 1.03-1.49 1.45-2.94 1.47-3.02-.03-.01-2.82-1.08-2.85-4.28zM14.52 4.3c.74-.9 1.24-2.15 1.1-3.39-1.06.04-2.35.71-3.12 1.6-.68.79-1.28 2.06-1.12 3.28 1.18.09 2.39-.6 3.14-1.49z" />
    </svg>
  );
}

type Props = {
  busy: boolean;
  onStart: () => void;
  onTokens: () => Promise<void> | void;
  onError: (message: string) => void;
};

export function AppleSignInButton({ busy, onStart, onTokens, onError }: Props) {
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const [hiddenNative, setHiddenNative] = useState(false);

  // Inside the iOS app the Apple sheet cannot complete until the backend Apple
  // provider has JWT credentials, so don't show testers a dead end.
  useEffect(() => {
    if (!NATIVE_APPLE_SIGN_IN_ENABLED && isNativeAppleAuthAvailable()) {
      setHiddenNative(true);
    }
  }, []);

  const finish = () => {
    inFlight.current = false;
    setPending(false);
  };

  useEffect(() => {
    if (!pending) return;
    let cancelled = false;
    const onFocus = () => {
      window.setTimeout(() => {
        void (async () => {
          if (cancelled) return;
          if (await hasLiveSession()) {
            finish();
            await onTokens();
            return;
          }
          if (cancelled) return;
          finish();
          onError("Apple sign-in didn't complete — try again or use email.");
        })();
      }, 1500);
    };
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [pending, onError, onTokens]);

  const click = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    onStart();
    setPending(true);
    try {
      // Inside the iOS app, use the native SocialLogin plugin (Apple's sheet)
      // — the web OAuth redirect flow does not complete inside a WKWebView.
      if (isNativeAppleAuthAvailable()) {
        const { error } = await signInWithNativeApple();
        finish();
        if (error) {
          onError(mapOAuthError(error, "Apple"));
          return;
        }
        await onTokens();
        return;
      }
      const result = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: window.location.origin,
      });
      if (result.redirected) return;
      if (result.error) {
        finish();
        onError(mapOAuthError(result.error, "Apple"));
        return;
      }
      finish();
      await onTokens();
    } catch (e) {
      finish();
      onError(mapOAuthError(e, "Apple"));
    }
  };

  if (hiddenNative) return null;

  return (
    <button
      type="button"
      onClick={() => void click()}
      disabled={busy}
      className="flex h-11 w-full items-center justify-center gap-3 rounded-xl bg-black text-white text-sm font-medium hover:bg-black/90 disabled:opacity-60"
    >
      <AppleGlyph />
      {busy ? "Connecting…" : "Continue with Apple"}
    </button>
  );
}
